import {
    isProcessed,
    isProcessing,
    WebData,
    webDataErrorSimple,
    webDataLoading,
    WebDataManipulator,
    webDataNotAsked,
    WebDataSteps,
    webDataSuccess,
} from "@lvt/web-data";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Done, ErrorOutline, Loop } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import { DateTime } from "luxon";
import { useState, useEffect, useRef, useContext } from "react";

import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import { VideoRetrievalContext } from "@/common/contexts/VideoRetrievalContext";
import { RootState, useSelector } from "@/store/index";
import { logError } from "@/utilities/api";
import * as FullStory from "@/utilities/hooks/fullstory";
import { ArchiveImagesNavMeta } from "@/utilities/types/archive";
import { Camera, isCamera } from "@/utilities/types/archive";


import { useStyles } from "./VideoRequestModalStyles";


interface VideoRequestModalProps {
    classes: Record<"paper", string>;
    id: string;
    onClose: () => void;
}

type CameraOption = {
    id: number;
    name: string;
    checked: boolean;
    request: WebData<null>;
};

export const VideoRequestModal: React.FC<VideoRequestModalProps> = (props: VideoRequestModalProps) => {
    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const videoRetrievalService = useContext(VideoRetrievalContext);
    const requestStartTime: DateTime = useSelector((state: RootState) =>
        DateTime.fromISO(state.archives.requestStartTime, { zone: unitMetadata.timezone }),
    );
    const requestDuration: number = useSelector((state: RootState) => state.archives.requestDuration);

    const cameraOptions: CameraOption[] = useSelector((state: RootState) =>
        new WebDataManipulator(state.archives.camerasOrCabinetsMeta)
            .map((cameraOrCabinetsMeta) =>
                cameraOrCabinetsMeta
                    .filter(
                        (cameraOrCabinetMeta) =>
                            isCamera(cameraOrCabinetMeta.record) && cameraOrCabinetMeta.record.canRequestVideo,
                    )
                    .map((cameraOrCabinetMeta) => {
                        const { id, name } = cameraOrCabinetMeta.record as Camera;

                        return { id, name, checked: false, request: webDataNotAsked() };
                    }),
            )
            .unwrapOr([]),
    );

    const amountOfCameras = cameraOptions.length;
    const classes = useStyles();
    const { onClose, ...other } = props;
    const radioGroupRef = useRef<HTMLElement>(null);

    const [cameraList, setCameraList] = useState<CameraOption[]>(cameraOptions);
    const checkedCameras = cameraList.filter((camera) => camera.checked);
    const camerasLoading = cameraList.reduce((sofar, camera) => sofar || isProcessing(camera.request), false);
    const camerasLoaded = cameraList.reduce((sofar, camera) => sofar || isProcessed(camera.request), false);

    const [allCamerasCheckedState, setAllCamerasCheckedState] = useState<boolean>(false);
    const [preventAllChangeCheckboxBehavior, setPreventAllChangeCheckboxBehavior] = useState<boolean>(false);
    const camerasCount = checkedCameras.length;

    const handleSingleCheck = (event: React.ChangeEvent<HTMLInputElement>, cameraId: number) => {
        //pull out cam you are trying to update
        const camToBeUpdated = cameraList.find((cam) => cam.id === cameraId);

        //update checked field
        if (camToBeUpdated) {
            camToBeUpdated.checked = event.target.checked;
        } else {
            // Send error to central logging
            console.log("Somehow not found", cameraId);

            return;
        }

        //then spread
        setCameraList([...cameraList]);

        if (allCamerasCheckedState && !event.target.checked) {
            //Unchecking of all liveunit cameras checkbox
            setPreventAllChangeCheckboxBehavior(true);
            setAllCamerasCheckedState(false);
        }
    };

    const handleCheckAllCameras = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAllCamerasCheckedState(event.target.checked); //apply useffects logic here and have a if condition in the single check
        setPreventAllChangeCheckboxBehavior(false);
    };

    useEffect(() => {
        if (camerasCount === amountOfCameras && !preventAllChangeCheckboxBehavior) {
            setPreventAllChangeCheckboxBehavior(true);
            setAllCamerasCheckedState(true);
        }
    }, [camerasCount]);

    useEffect(() => {
        //Updates other checkboxes following handleCheckAllCameras fn
        if (preventAllChangeCheckboxBehavior) {
            setPreventAllChangeCheckboxBehavior(false);
        } else {
            cameraList.map((camera) => {
                camera.checked = allCamerasCheckedState;
            });

            setCameraList([...cameraList]);
        }
    }, [allCamerasCheckedState]);

    const handleEntering = () => {
        if (radioGroupRef.current != null) {
            radioGroupRef.current.focus();
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const [isMounted, setIsMounted] = useState(true);

    useEffect(() => {
        return () => setIsMounted(false);
    }, []);

    const handleOk = () => {
        const requestStartTimeSeconds = requestStartTime.toSeconds();
        const checkedIds = checkedCameras.map((camera) => camera.id);

        setCameraList((currentCameraList) => {
            return currentCameraList.map((camera) => {
                if (checkedIds.includes(camera.id)) {
                    return { ...camera, request: webDataLoading() };
                }

                return camera;
            });
        });

        checkedCameras.map((camera) => {
            const title = `${requestStartTime.toFormat("HH:mm:ss - MMM d, yyyy - ZZZZ")} Duration: ${Math.trunc(
                requestDuration,
            )} Seconds`;

            videoRetrievalService
                .requestVideoRetrieval(
                    camera.id,
                    title,
                    requestStartTime,
                    DateTime.fromSeconds(requestStartTimeSeconds + requestDuration),
                )
                .then((result) => {
                    if (isMounted) {
                        if (result) {
                            setCameraList((currentCameraList) => {
                                return currentCameraList.map((cammeraListCamera) => {
                                    if (camera.id == cammeraListCamera.id) {
                                        logError(null, result);

                                        return {
                                            ...cammeraListCamera,
                                            request: webDataErrorSimple({
                                                message: "Unable to request video at this time.",
                                            }),
                                        };
                                    }

                                    return cammeraListCamera;
                                });
                            });
                        } else {
                            setCameraList((currentCameraList) => {
                                return currentCameraList.map((cammeraListCamera) => {
                                    if (camera.id == cammeraListCamera.id) {
                                        return {
                                            ...cammeraListCamera,
                                            request: webDataSuccess(null),
                                        };
                                    }

                                    return cammeraListCamera;
                                });
                            });
                        }
                    }
                });
        });
    };

    const handleDone = () => {
        onClose();
    };

    const showCameraIcon = (request: WebData<null>) => {
        switch (request.type) {
            case WebDataSteps.Loading:
                return (
                    <span className={`${classes.loading} ${classes.cameraRequestStatus}`}>
                        <Loop />
                        &nbsp; Loading
                    </span>
                );
            case WebDataSteps.Success:
                return (
                    <span className={`${classes.success} ${classes.cameraRequestStatus}`}>
                        <Done />
                        &nbsp; Success
                    </span>
                );
            case WebDataSteps.Error:
                return (
                    <span className={`${classes.failed} ${classes.cameraRequestStatus}`}>
                        <ErrorOutline />
                        &nbsp; Failed
                    </span>
                );
            default:
    <></>;
        }
    };

    const selectionDurationInMinutes = requestDuration / 60;

    const partTwoDateFormat = Object.assign({
        hour: "numeric",
        minute: "2-digit",
    });

    return (
        <Dialog
            disableBackdropClick
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth={true}
            onEntering={handleEntering}
            aria-labelledby="video-confirmation"
            open={true}
            {...other}
        >
            <DialogTitle id="video-confirmation-title">
                <div className={classes.header}>
                    Confirm Video Request
                    {!camerasLoading && !camerasLoaded && (
                        <CloseIcon className={classes.icon} color="primary" onClick={handleCancel} />
                    )}
                </div>
            </DialogTitle>
            <DialogContent className={classes.content} dividers>
                <div className={classes.selection}>
                    Selection (
                    {selectionDurationInMinutes >= 2
                        ? `${Math.round(selectionDurationInMinutes)} minutes`
                        : `${Math.round(selectionDurationInMinutes)} minute`}
                    )
                </div>
                <div>
                    {requestStartTime.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)} {"- "}
                    {requestStartTime.plus({ minutes: selectionDurationInMinutes }).toLocaleString(partTwoDateFormat)}
                    {", "}
                    {requestStartTime
                        .plus({ minutes: selectionDurationInMinutes })
                        .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                </div>
                <h3>Request video from</h3>
                <FormControlLabel
                    control={
                        <Checkbox
                            name="All Live Unit Cameras"
                            checked={allCamerasCheckedState}
                            onChange={handleCheckAllCameras}
                            disabled={camerasLoading || camerasLoaded}
                        />
                    }
                    label="All Live Unit Cameras"
                />
            </DialogContent>

            <DialogContent className={classes.content} dividers>
                <FormControl component="fieldset">
                    <div>
                        <table>
                            <tbody>
                                {cameraList.map((camera) => (
                                    <tr key={camera.id}>
                                        <td>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name={camera.name}
                                                        checked={camera.checked}
                                                        disabled={camerasLoading || camerasLoaded}
                                                        onChange={(event) => handleSingleCheck(event, camera.id)}
                                                    />
                                                }
                                                label={camera.name}
                                            />
                                        </td>
                                        <td>{showCameraIcon(camera.request)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FormControl>
            </DialogContent>
            <DialogActions className={classes.modalFooter}>
                {camerasLoading || camerasLoaded ? (
                    <Button onClick={handleDone} disabled={!camerasLoaded} color="secondary" variant="contained">
                        Done
                    </Button>
                ) : (
                    <>
                        <Button autoFocus onClick={handleCancel} color="primary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleOk}
                            className={FullStory.names.classes.history.requestVideo}
                            disabled={checkedCameras.length <= 0}
                            color="secondary"
                            variant="contained"
                        >
                            Request Video
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default VideoRequestModal;
